import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, UploadCloud, X, Loader2, Image as ImageIcon, Users } from 'lucide-react';
import { useCreateServiceMutation, useUpdateServiceMutation, useUploadFileMutation } from '../../../redux/actions/Admin/ServiceAdmin';
import { ICreateServiceRequest } from '../../../types/Request/AdminRequest';
import { IserviceResponse } from '../../../types/Response/AdminResponse';

interface ServiceFormScreenProps {
  mode: 'add' | 'edit';
  initialData: IserviceResponse | null;
  onClose: () => void;
}

// Map key matching exact C# Enum string names to human-readable UI labels
const SERVICE_TYPE_OPTIONS = [
  { value: 'Grooming', label: 'Grooming' },
  { value: 'Training', label: 'Training' },
  { value: 'VetCare', label: 'Vet Care' },
  { value: 'PetTaxi', label: 'Pet Taxi' },
  { value: 'Relocation', label: 'Relocation' },
  { value: 'Walking', label: 'Walking' },
  { value: 'DayCare', label: 'Day Care' },
  { value: 'Other', label: 'Other' },
];

export default function ServiceFormScreen({ mode, initialData, onClose }: ServiceFormScreenProps) {
  const [createService, { isLoading: isCreating }] = useCreateServiceMutation();
  const [updateService, { isLoading: isUpdating }] = useUpdateServiceMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  
  const isProcessing = isCreating || isUpdating || isUploading;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<ICreateServiceRequest>({
    name: '',
    description: '',
    logoUrl: '',
    serviceType: 'Grooming', // String matching C# enum identifier
    capacity: 1,
    isActive: true,
  });

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData({
        id: initialData.id,
        name: initialData.name,
        description: initialData.description,
        logoUrl: initialData.logoUrl,
        serviceType: initialData.serviceType || 'Grooming',
        capacity: initialData.capacity,
        isActive: initialData.isActive,
      });
    }
  }, [mode, initialData]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const response = await uploadFile({
        File: file,
        FolderName: "Services/Avatars"
      }).unwrap();

      setFormData(prev => ({ ...prev, logoUrl: response.fileUrl }));
    } catch (error) {
      console.error("Failed to upload service avatar asset:", error);
      alert("Image asset upload failed. Please retry.");
    }
  };

  const handleRemoveImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFormData(prev => ({ ...prev, logoUrl: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.description || !formData.serviceType) return;

    try {
      if (mode === 'edit') {
        await updateService(formData).unwrap();
      } else {
        await createService(formData).unwrap();
      }
      onClose();
    } catch (error) {
      console.error(`API error during submission [${mode}]:`, error);
    }
  };

  return (
    <div className="min-h-screen p-6 flex items-center justify-center w-full relative">
      
      {/* Loading Overlay */}
      {isProcessing && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-50 flex items-center justify-center rounded-2xl">
          <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-5 h-5 animate-spin text-[#6DBE45]" />
            <span className="font-semibold text-sm text-slate-700">
              {isUploading ? "Uploading image asset..." : "Saving service details..."}
            </span>
          </div>
        </div>
      )}

      <div className="max-w-xl w-full space-y-6">
        
        {/* Back Button */}
        <button
          type="button"
          onClick={onClose}
          disabled={isProcessing}
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors cursor-pointer group disabled:opacity-50"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform stroke-[2.5]" />
          Back to Dashboard
        </button>

        {/* Card Form Wrapper */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          
          <div className="bg-white p-5 border-b border-gray-100">
            <h2 className="text-2xl font-bold text-[#234F1E]">
              {mode === 'edit' ? 'Edit Service Details' : 'Create New Service'}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            
            {/* Round Avatar Picker */}
            <div className="flex flex-col items-center space-y-2">
              <label className="text-sm font-semibold text-gray-600 w-full text-left">
                Service Avatar
              </label>
              
              <input 
                type="file"
                ref={fileInputRef}
                onChange={handleImageChange}
                accept="image/*"
                className="hidden"
                disabled={isProcessing}
              />

              <div 
                onClick={() => !isProcessing && fileInputRef.current?.click()}
                className={`w-32 h-32 rounded-full border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all relative overflow-hidden group bg-gray-50 ${
                  formData.logoUrl ? 'border-[#6DBE45] bg-white' : 'border-gray-300 hover:border-[#6DBE45]'
                }`}
              >
                {formData.logoUrl ? (
                  <>
                    <img 
                      src={formData.logoUrl} 
                      alt="Service asset profile" 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <UploadCloud className="w-6 h-6 text-white" />
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      disabled={isProcessing}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full shadow-md transition hover:bg-red-600 disabled:opacity-50"
                      title="Remove Image"
                    >
                      <X className="w-3.5 h-3.5 stroke-[2.5]" />
                    </button>
                  </>
                ) : (
                  <div className="text-center p-4 flex flex-col items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-gray-400 group-hover:text-[#6DBE45] transition-colors" />
                    <span className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-wider group-hover:text-[#6DBE45]">
                      Choose File
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Service Name Input */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-600">Service Name</label>
              <input
                type="text"
                required
                disabled={isProcessing}
                placeholder="e.g., Premium Grooming, Pet Taxi"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none font-medium text-sm transition focus:border-[#6DBE45] disabled:bg-gray-50"
              />
            </div>

            {/* Service Type Dropdown & Capacity Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Service Type Dropdown */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-600">Service Type</label>
                <select
                  required
                  disabled={isProcessing}
                  value={formData.serviceType}
                  onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none font-medium text-sm transition focus:border-[#6DBE45] bg-white disabled:bg-gray-50 cursor-pointer text-gray-700"
                >
                  {SERVICE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Capacity Input */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-gray-600">Capacity</label>
                <input
                  type="number"
                  min={0}
                  required
                  disabled={isProcessing}
                  placeholder="e.g., 5, 10"
                  value={formData.capacity ?? 0}
                  onChange={(e) => setFormData({ ...formData, capacity: Number(e.target.value) })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none font-medium text-sm transition focus:border-[#6DBE45] disabled:bg-gray-50 text-gray-800"
                />
              </div>
            </div>

            {/* Description Area */}
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-600">Service Description</label>
              <textarea
                required
                rows={3}
                disabled={isProcessing}
                placeholder="Provide a brief summary breakdown regarding what this module contains..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none text-sm font-medium transition focus:border-[#6DBE45] resize-none disabled:bg-gray-50"
              ></textarea>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                disabled={isProcessing}
                className="border border-gray-300 bg-white px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-700 cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              
              <button
                type="submit"
                disabled={isProcessing}
                className="bg-[#6DBE45] text-white px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm hover:opacity-90 transition cursor-pointer inline-flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                {mode === 'edit' ? 'Update Service' : 'Save Service'}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}